// Enhanced CreateMaintenanceModal with Dashboard Approach
import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, User, Phone, AlertTriangle, Wrench } from 'lucide-react';
import { maintenanceService } from '../../services/maintenanceService';
import { masterSupabase } from '../../lib/supabase-master.js';

const CreateMaintenanceModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  selectedOwner = 'all',
  editingTask = null // New prop for edit mode
}) => {
  const [formData, setFormData] = useState({
    owner_id: selectedOwner,
    property_id: '',
    unit_id: '',
    title: '',
    description: '',
    maintenance_type: '',
    priority: 'mittel',
    scheduled_date: '',
    estimated_cost: '',
    contractor_name: '',
    contractor_contact: ''
  });

  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  // Determine if we're in edit mode
  const isEditMode = editingTask !== null;

  // Load editing task data when in edit mode
  useEffect(() => {
    if (isEditMode && editingTask) {
      setFormData({
        owner_id: selectedOwner,
        property_id: editingTask.property_id || '',
        unit_id: editingTask.unit_id || '',
        title: editingTask.title || '',
        description: editingTask.description || '',
        maintenance_type: editingTask.maintenance_type || '',
        priority: editingTask.priority || 'mittel',
        scheduled_date: editingTask.scheduled_date ? editingTask.scheduled_date.split('T')[0] : '',
        estimated_cost: editingTask.estimated_cost || '',
        contractor_name: editingTask.contractor_name || '',
        contractor_contact: editingTask.contractor_contact || ''
      });
    } else {
      // Reset form for create mode
      setFormData({
        owner_id: selectedOwner,
        property_id: '',
        unit_id: '',
        title: '',
        description: '',
        maintenance_type: '',
        priority: 'mittel',
        scheduled_date: '',
        estimated_cost: '',
        contractor_name: '',
        contractor_contact: ''
      });
    }
  }, [isEditMode, editingTask, selectedOwner]);

  // Load owners when modal opens - DASHBOARD APPROACH
  useEffect(() => {
    if (isOpen) {
      loadOwners();
    }
  }, [isOpen]);

  // Load properties when owner changes - DASHBOARD APPROACH
  useEffect(() => {
    if (isOpen && formData.owner_id) {
      loadProperties();
    }
  }, [isOpen, formData.owner_id]);

  // Load units when property changes
  useEffect(() => {
    if (formData.property_id) {
      loadUnits(formData.property_id);
    } else {
      setUnits([]);
    }
  }, [formData.property_id]);

  // DASHBOARD APPROACH: Direct masterSupabase access
  const loadOwners = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading owners (Dashboard approach)...');
      
      // EXACT SAME AS DASHBOARD
      const { data: ownersData, error } = await masterSupabase.from('owners').select('*').order('name');
      
      console.log('✅ Owners loaded (Dashboard approach):', ownersData?.length || 0);
      console.log('🔍 Owners data:', ownersData);
      
      if (error) {
        console.error('❌ Error loading owners:', error);
      }
      
      setOwners(ownersData || []);
    } catch (error) {
      console.error('❌ Error loading owners (catch):', error);
      setOwners([]);
    } finally {
      setLoading(false);
    }
  };

  // DASHBOARD APPROACH: Direct masterSupabase access for properties
  const loadProperties = async () => {
    try {
      console.log('🔍 Loading properties for owner (Dashboard approach):', formData.owner_id);
      
      // EXACT SAME AS DASHBOARD
      let propertiesQuery = masterSupabase.from('properties').select('*');
      if (formData.owner_id !== 'all') {
        propertiesQuery = propertiesQuery.eq('owner_id', formData.owner_id);
      }
      const { data: properties, error: propError } = await propertiesQuery;
      
      console.log('✅ Properties loaded (Dashboard approach):', properties?.length || 0);
      console.log('🔍 Properties data:', properties);
      
      if (propError) {
        console.error('❌ Properties error:', propError);
        setProperties([]);
        return;
      }
      
      setProperties(properties || []);
    } catch (error) {
      console.error('❌ Error loading properties (catch):', error);
      setProperties([]);
    }
  };

  const loadUnits = async (propertyId) => {
    try {
      console.log('🔍 Loading units for property:', propertyId);
      
      // Use masterSupabase directly like dashboard
      const { data: unitsData, error } = await masterSupabase
        .from('units')
        .select('*')
        .eq('property_id', propertyId);
      
      console.log('✅ Units loaded:', unitsData?.length || 0);
      
      if (error) {
        console.error('❌ Error loading units:', error);
      }
      
      setUnits(unitsData || []);
    } catch (error) {
      console.error('❌ Error loading units (catch):', error);
      setUnits([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Reset property and unit when owner changes
    if (name === 'owner_id') {
      setFormData(prev => ({
        ...prev,
        property_id: '',
        unit_id: ''
      }));
      setProperties([]);
      setUnits([]);
    }

    // Reset unit when property changes
    if (name === 'property_id') {
      setFormData(prev => ({
        ...prev,
        unit_id: ''
      }));
      setUnits([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Titel ist erforderlich';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Beschreibung ist erforderlich';
    }

    if (!formData.maintenance_type) {
      newErrors.maintenance_type = 'Wartungstyp ist erforderlich';
    }

    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'Geplantes Datum ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const taskData = {
        property_id: formData.property_id || null,
        unit_id: formData.unit_id || null,
        title: formData.title.trim(),
        description: formData.description.trim(),
        maintenance_type: formData.maintenance_type,
        priority: formData.priority,
        scheduled_date: formData.scheduled_date,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        contractor_name: formData.contractor_name.trim() || null,
        contractor_contact: formData.contractor_contact.trim() || null
      };

      if (isEditMode) {
        await maintenanceService.updateMaintenanceTask(editingTask.id, taskData);
      } else {
        await maintenanceService.createMaintenanceTask(taskData);
      }
      
      // Success feedback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal
      onClose();
      
    } catch (error) {
      console.error('❌ Error saving maintenance task:', error);
      setErrors({ submit: 'Fehler beim Speichern der Wartung' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Transparent Backdrop */}
      <div className="absolute inset-0 bg-gray-900/75 dark:bg-black/75" onClick={onClose} />
      
      {/* Modal Content */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditMode ? "Wartung bearbeiten" : "Neue Wartung erstellen"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Owner Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Eigentümer
            </label>
            <select
              name="owner_id"
              value={formData.owner_id}
              onChange={handleInputChange}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            >
              <option value="all">Alle Eigentümer</option>
              {owners.map(owner => (
                <option key={owner.id} value={owner.id}>
                  {owner.name || owner.name || `Owner ${owner.id}`}
                </option>
              ))}
            </select>
            {loading && (
              <p className="mt-1 text-sm text-gray-500">Lade Eigentümer...</p>
            )}
            {owners.length === 0 && !loading && (
              <p className="mt-1 text-sm text-red-500">Keine Eigentümer gefunden</p>
            )}
          </div>

          {/* Property and Unit Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Objekt (optional)
              </label>
              <select
                name="property_id"
                value={formData.property_id}
                onChange={handleInputChange}
                disabled={!formData.owner_id || formData.owner_id === 'all'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              >
                <option value="">Objekt auswählen</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>
                    {property.property_name || property.property_name || `Property ${property.id}`}
                  </option>
                ))}
              </select>
              {properties.length === 0 && formData.owner_id !== 'all' && (
                <p className="mt-1 text-sm text-gray-500">Keine Objekte für diesen Eigentümer</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Einheit (optional)
              </label>
              <select
                name="unit_id"
                value={formData.unit_id}
                onChange={handleInputChange}
                disabled={!formData.property_id}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              >
                <option value="">Einheit auswählen</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unit_identifier || unit.unit_number || `Unit ${unit.id}`}
                  </option>
                ))}
              </select>
              {units.length === 0 && formData.property_id && (
                <p className="mt-1 text-sm text-gray-500">Keine Einheiten für dieses Objekt</p>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titel *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="z.B. Heizung reparieren"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beschreibung *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="Detaillierte Beschreibung der Wartungsarbeiten"
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          {/* Maintenance Type and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Wrench className="w-4 h-4 inline mr-2" />
                Wartungstyp *
              </label>
              <select
                name="maintenance_type"
                value={formData.maintenance_type}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                  errors.maintenance_type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Typ auswählen</option>
                <option value="reparatur">Reparatur</option>
                <option value="wartung">Wartung</option>
                <option value="inspektion">Inspektion</option>
                <option value="reinigung">Reinigung</option>
                <option value="heizung">Heizung</option>
                <option value="elektrik">Elektrik</option>
                <option value="sanitaer">Sanitär</option>
                <option value="garten">Garten</option>
                <option value="dach">Dach</option>
                <option value="sonstiges">Sonstiges</option>
              </select>
              {errors.maintenance_type && <p className="mt-1 text-sm text-red-600">{errors.maintenance_type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Priorität
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="niedrig">Niedrig</option>
                <option value="mittel">Mittel</option>
                <option value="hoch">Hoch</option>
                <option value="kritisch">Kritisch</option>
              </select>
            </div>
          </div>

          {/* Scheduled Date and Estimated Cost */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Geplantes Datum *
              </label>
              <input
                type="date"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                  errors.scheduled_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.scheduled_date && <p className="mt-1 text-sm text-red-600">{errors.scheduled_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <DollarSign className="w-4 h-4 inline mr-2" />
                Geschätzte Kosten (€)
              </label>
              <input
                type="number"
                name="estimated_cost"
                value={formData.estimated_cost}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Contractor Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Auftragnehmer
              </label>
              <input
                type="text"
                name="contractor_name"
                value={formData.contractor_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Name des Auftragnehmers"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Kontakt
              </label>
              <input
                type="text"
                name="contractor_contact"
                value={formData.contractor_contact}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Telefon oder E-Mail"
              />
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.submit}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Speichern...' : (isEditMode ? "Wartung aktualisieren" : "Wartung erstellen")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMaintenanceModal;

